import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect } from 'react';
import { registerSchema, type RegisterInput } from '@munawb/validators';
import { supabase } from '@munawb/supabase';

type Department = { id: string; name: string };

export default function RegisterPage() {
  const [departments, setDepartments]   = useState<Department[]>([]);
  const [serverError, setServerError]   = useState<string | null>(null);
  const [submitted, setSubmitted]       = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({ resolver: zodResolver(registerSchema) });

  // Load departments for the dropdown
  useEffect(() => {
    supabase
      .from('departments')
      .select('id, name')
      .eq('active', true)
      .order('name')
      .then(({ data }) => {
        if (data) setDepartments(data);
      });
  }, []);

  const onSubmit = async (data: RegisterInput) => {
    setServerError(null);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-register`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name:     data.name,
            email:    data.email,
            phone:    data.phone   || undefined,
            dept_id:  data.dept_id || undefined,
            title:    data.title   || undefined,
            password: data.password,
          }),
        }
      );
      const json = await res.json();
      if (!res.ok) {
        setServerError(json.error ?? 'حدث خطأ. يرجى المحاولة مجدداً.');
        return;
      }
      setSubmitted(true);
    } catch {
      setServerError('تعذّر الاتصال بالخادم. تحقق من اتصالك بالإنترنت.');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950 p-4">
        <div className="glass-panel w-full max-w-md rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-black text-slate-800 dark:text-white mb-2">تم إرسال طلبك</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            سيتم مراجعة طلبك من قِبَل المدير وإشعارك بالبريد الإلكتروني عند الموافقة.
          </p>
          <a href="/" className="btn-primary">العودة لتسجيل الدخول</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950 p-4">
      <div className="glass-panel w-full max-w-lg rounded-3xl p-8 shadow-2xl">

        {/* Logo + Title */}
        <div className="text-center mb-8">
          <img src="/munawb-logo.png" alt="مُناوب" className="w-16 h-16 mx-auto mb-4 rounded-2xl shadow-md" />
          <h1 className="text-2xl font-black text-slate-800 dark:text-white">طلب حساب جديد</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-bold mt-1">
            سيتم مراجعة طلبك من قِبَل مدير النظام
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>

          {/* Name */}
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              الاسم الكامل <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="محمد عبدالله العمري"
              className="input-field"
              {...register('name')}
            />
            {errors.name && <p className="text-red-500 text-xs font-bold mt-1">{errors.name.message}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              البريد الإلكتروني <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              autoComplete="email"
              placeholder="example@hospital.com"
              className="input-field"
              dir="ltr"
              {...register('email')}
            />
            {errors.email && <p className="text-red-500 text-xs font-bold mt-1">{errors.email.message}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              رقم الجوال
            </label>
            <input
              type="tel"
              placeholder="05XXXXXXXX"
              className="input-field"
              dir="ltr"
              {...register('phone')}
            />
            {errors.phone && <p className="text-red-500 text-xs font-bold mt-1">{errors.phone.message}</p>}
          </div>

          {/* Department */}
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              القسم
            </label>
            <select className="input-field" {...register('dept_id')}>
              <option value="">-- اختر القسم --</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              المسمى الوظيفي
            </label>
            <input
              type="text"
              placeholder="مشرف سلامة المرضى"
              className="input-field"
              {...register('title')}
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              كلمة المرور <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="8 أحرف على الأقل"
              className="input-field"
              dir="ltr"
              {...register('password')}
            />
            {errors.password && <p className="text-red-500 text-xs font-bold mt-1">{errors.password.message}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              تأكيد كلمة المرور <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              className="input-field"
              dir="ltr"
              {...register('confirm_password')}
            />
            {errors.confirm_password && (
              <p className="text-red-500 text-xs font-bold mt-1">{errors.confirm_password.message}</p>
            )}
          </div>

          {/* Server Error */}
          {serverError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 text-center">
              <p className="text-red-600 dark:text-red-400 text-sm font-bold">{serverError}</p>
            </div>
          )}

          {/* Submit */}
          <button type="submit" className="btn-primary w-full py-3 text-base" disabled={isSubmitting}>
            {isSubmitting ? (
              <><span className="spinner border-white/30 border-t-white" /> جارٍ إرسال الطلب...</>
            ) : (
              'إرسال طلب التسجيل'
            )}
          </button>
        </form>

        <div className="mt-5 text-center text-xs font-bold">
          <a href="/" className="text-blue-600 dark:text-blue-400 hover:underline">
            لدي حساب — تسجيل الدخول
          </a>
        </div>

      </div>
    </div>
  );
}
